<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <div v-if="props.label" class="combobox-label" :class="{ 'is-required': props.required }">
      {{ props.label }}
      <span v-if="props.required" class="required-indicator">*</span>
    </div>

    <div class="entity-combobox" ref="comboboxRef" v-bind="filteredAttrs">
      <div
        class="combobox-input-wrapper"
        :class="{
          'is-open': isOpen,
          'has-selection': !isOpen && hasSelection,
          'has-avatar':
            (!isOpen && hasSelection && shouldShowAvatar(selectedOption)) ||
            (freeTextEntry && freeTextMatchedItem && shouldShowAvatar(freeTextMatchedItem)),
          'has-error': Array.isArray(error) ? error.length > 0 : !!error,
          'is-disabled': disabled,
        }"
        @click="handleTriggerClick">
        <!-- Free text mode -->
        <template v-if="freeTextEntry">
          <div
            v-if="freeTextMatchedItem && shouldShowAvatar(freeTextMatchedItem)"
            class="entity-avatar"
            :class="getAvatarBgClass(freeTextMatchedItem)">
            <img
              v-if="freeTextMatchedItem.imageUrl"
              :src="freeTextMatchedItem.imageUrl"
              :alt="resolveLabel(freeTextMatchedItem)" />
            <span v-else class="initials">{{ getInitialsForItem(freeTextMatchedItem) }}</span>
          </div>
          <input
            ref="inputRef"
            v-model="freeTextValue"
            type="text"
            class="combobox-input"
            :placeholder="placeholder"
            @input="handleFreeTextInput"
            @keydown.down.prevent="navigate('down')"
            @keydown.up.prevent="navigate('up')"
            @keydown.enter.prevent.stop="handleFreeTextEnter"
            @keydown.esc.prevent="closeDropdown"
            @focus="openDropdown"
            @blur="handleInputBlur" />
        </template>

        <!-- Standard combobox mode -->
        <template v-else>
          <input
            v-if="isOpen"
            ref="inputRef"
            v-model="searchQuery"
            type="text"
            class="combobox-input"
            :placeholder="hasSelection && selectedOption ? resolveLabel(selectedOption) : placeholder"
            @keydown.down.prevent="navigate('down')"
            @keydown.up.prevent="navigate('up')"
            @keydown.enter.prevent.stop="selectHighlighted"
            @keydown.esc.prevent="closeDropdown"
            @blur="handleInputBlur" />

          <div v-else class="selection-display">
            <template v-if="hasSelection && selectedOption">
              <div
                v-if="shouldShowAvatar(selectedOption)"
                class="entity-avatar"
                :class="getAvatarBgClass(selectedOption)">
                <img
                  v-if="selectedOption.imageUrl"
                  :src="selectedOption.imageUrl"
                  :alt="resolveLabel(selectedOption)" />
                <span v-else class="initials">{{ getInitialsForItem(selectedOption) }}</span>
              </div>
              <span class="selected-name">{{ resolveLabel(selectedOption) }}</span>
            </template>
            <span v-else class="placeholder-text">{{ placeholder }}</span>
          </div>
        </template>

        <div v-if="loading" class="combobox-inline-spinner" aria-label="Loading">
          <div class="inline-spinner-circle"></div>
        </div>
        <button
          v-else-if="showClear"
          type="button"
          class="combobox-clear"
          @mousedown.prevent
          @click.stop="handleClear"
          tabindex="-1"
          aria-label="Clear selection">
          <IconX size="16" />
        </button>
        <button
          v-else
          type="button"
          class="combobox-chevron"
          :disabled="disabled"
          @mousedown.prevent
          @click.stop="handleChevronToggle"
          tabindex="-1"
          :aria-label="isOpen ? 'Close dropdown' : 'Open dropdown'">
          <IconChevronDown class="chevron-icon" :class="{ 'rotate-180': isOpen }" size="16" />
        </button>
      </div>

      <transition name="fade">
        <div v-if="isOpen" class="combobox-dropdown">
          <div v-if="loading && flatVisible.length === 0" class="loading-state">
            <div class="spinner"></div>
            <p>Loading...</p>
          </div>

          <template v-else-if="flatVisible.length > 0">
            <div class="tree-scroll-breadcrumb" v-show="scrollBreadcrumb">{{ scrollBreadcrumb }}</div>
            <div class="dropdown-scroll-area" ref="scrollAreaRef" @scroll="onDropdownScroll">
              <ul class="dropdown-list tree-root-list" role="listbox">
                <!-- Free-text entry: what the user typed is itself a valid choice,
                     so it gets a row of its own instead of an empty dropdown. -->
                <FreeTextCreateOption
                  v-if="freeTextCreateOption"
                  :label="resolveLabel(freeTextCreateOption)"
                  :highlighted="isCreateRowHighlighted"
                  @mousedown.prevent="selectItem(freeTextCreateOption)"
                  @mouseenter="highlightedIndex = 0" />

                <LeadTreeNode
                  v-for="node in displayTree"
                  :key="resolveValue(node)"
                  :node="node"
                  :selected-node="selectedOption"
                  :highlighted-node-id="highlightedNodeId"
                  :expanded-nodes="expandedNodes"
                  :search-value="searchValue"
                  :display-initials="displayInitials"
                  :get-option-label="getOptionLabel"
                  :get-option-value="getOptionValue"
                  @select-node="selectItem"
                  @toggle-expand="toggleExpand"
                  @highlight-node="handleHighlightNode" />
              </ul>
            </div>
          </template>

          <NoResults v-else :search-query="searchValue" :message="props.emptyMessage">
            <template v-if="$slots['no-results']" #no-results="{ searchQuery: sq }">
              <slot name="no-results" :searchQuery="sq" />
            </template>
          </NoResults>
        </div>
      </transition>
    </div>
    <FormErrorList
      v-if="error && (Array.isArray(error) ? error.length > 0 : true)"
      :errors="Array.isArray(error) ? error : [error]" />
    <div v-if="$slots.hint" class="hint-text">
      <slot name="hint"></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useAttrs, computed, ref, nextTick, onMounted, onUnmounted, watch } from 'vue';
import { useDropdownManager } from '@/composables/useDropdownManager';
import { IconChevronDown, IconX } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';
import NoResults from './NoResults.vue';
import FreeTextCreateOption from './FreeTextCreateOption.vue';
import LeadTreeNode from './LeadTreeNode.vue';
import { getInitials } from '@/helpers/helpers';
import {
  freeTextCreateLabel,
  isFreeTextCreateOption,
  useFreeTextCreateOption,
} from '@/composables/useFreeTextCreateOption';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const filteredAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: className, style, ...rest } = attrs;
  return rest;
});

interface OptionItem {
  id?: string | number;
  name?: string;
  imageUrl?: string;
  initials?: string;
  colorVariant?: string;
  children?: OptionItem[];
  isExpanded?: boolean;
  [key: string]: any;
}

const props = withDefaults(
  defineProps<{
    label?: string;
    modelValue: OptionItem | string | null;
    items: OptionItem[];
    placeholder?: string;
    displayInitials?: boolean;
    getOptionLabel?: string | ((option: OptionItem) => string);
    getOptionValue?: string | ((option: OptionItem) => any);
    reduce?: (option: OptionItem) => any;
    loading?: boolean;
    error?: string | string[] | null;
    required?: boolean;
    searchMode?: 'local' | 'remote';
    disabled?: boolean;
    freeTextEntry?: boolean;
    clearable?: boolean;
    emptyMessage?: string;
  }>(),
  {
    searchMode: 'remote',
  }
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
  (e: 'search', query: string): void;
  (e: 'option:selected', option: OptionItem): void;
  (e: 'option:created', value: string): void;
  (e: 'open'): void;
  (e: 'close'): void;
}>();

const { isOpen, open: openDropdownManager, close: closeDropdownManager } = useDropdownManager();
const freeTextValue = ref('');
let lastEmittedSearch: string | null = null;

const emitSearch = (query: string) => {
  if (query === lastEmittedSearch) return;
  lastEmittedSearch = query;
  emit('search', query);
};

const resolveLabel = (option: OptionItem | null | undefined): string => {
  if (!option) return '';
  const createLabel = freeTextCreateLabel(option);
  if (createLabel !== null) return createLabel;
  if (typeof props.getOptionLabel === 'function') return props.getOptionLabel(option);
  const key = (props.getOptionLabel as string) || 'name';
  return option[key] ?? '';
};

const resolveValue = (option: OptionItem | null | undefined): any => {
  if (!option) return null;
  if (typeof props.getOptionValue === 'function') return props.getOptionValue(option);
  const key = (props.getOptionValue as string) || 'id';
  return option[key];
};

const searchQuery = ref('');
// Unified search term — either the combobox search input or the free-text value.
const searchValue = computed(() => (props.freeTextEntry ? freeTextValue.value : searchQuery.value));

const highlightedIndex = ref(-1);
const comboboxRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const scrollAreaRef = ref<HTMLElement | null>(null);
const scrollBreadcrumb = ref('');

// Single source of truth for which nodes are expanded. Seeded automatically
// whenever new server results arrive (see watch(props.items) below) — user
// toggles then persist against that seed until the next server response.
const expandedNodes = ref<(string | number)[]>([]);

// Collect every non-leaf node id in a tree, for auto-expand-on-new-results.
const collectNonLeafIds = (nodes: OptionItem[]): (string | number)[] => {
  const ids: (string | number)[] = [];
  const walk = (ns: OptionItem[]) => {
    for (const n of ns) {
      if (n.children && n.children.length > 0) {
        ids.push(resolveValue(n));
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return ids;
};

watch(
  () => props.items,
  (newItems) => {
    // Whenever the server returns a fresh result set, auto-expand every
    // non-leaf so matches are visible without the user clicking chevrons.
    // User toggles between responses stay intact because they mutate the same
    // array we're about to overwrite — the overwrite is the "reset".
    expandedNodes.value = collectNonLeafIds(newItems);
  },
  { deep: false }
);

// Local-search recursive filter. Mirrors TreeSelect.vue: a node is kept if it
// matches itself OR any descendant matches; in the latter case the node is
// marked isExpanded=true so LeadTreeNode expands it regardless of user state.
const filterNodesLocal = (nodes: OptionItem[], lower: string): OptionItem[] => {
  const results: OptionItem[] = [];
  nodes.forEach((node) => {
    const matchesSelf = resolveLabel(node).toLowerCase().includes(lower);
    let filteredChildren: OptionItem[] = [];
    if (node.children && node.children.length > 0) {
      filteredChildren = filterNodesLocal(node.children, lower);
    }
    if (matchesSelf || filteredChildren.length > 0) {
      const newNode = { ...node };
      if (matchesSelf && node.children) {
        newNode.children = node.children.map((c) => ({ ...c }));
      } else {
        newNode.children = filteredChildren;
        newNode.isExpanded = true;
      }
      results.push(newNode);
    }
  });
  return results;
};

// shared with LeadCombobox — see useFreeTextCreateOption for when the row is
// offered and why. matching walks the whole tree, so a nested option counts as
// an existing one.
const freeTextCreateOption = useFreeTextCreateOption({
  enabled: () => props.freeTextEntry,
  loading: () => props.loading,
  text: () => freeTextValue.value,
  hasExactMatch: (label) => !!findNodeByValueOrLabel(props.items, label),
});

const isCreateRowHighlighted = computed(() => !!freeTextCreateOption.value && highlightedIndex.value === 0);

const displayTree = computed<OptionItem[]>(() => {
  if (props.searchMode === 'remote' || !searchValue.value) return props.items;
  return filterNodesLocal(props.items, searchValue.value.toLowerCase());
});

// Depth-first walk over displayTree, emitting only currently-visible nodes
// (root + descendants of expanded branches). Drives keyboard navigation and
// highlight-by-index.
const flatVisible = computed<OptionItem[]>(() => {
  const out: OptionItem[] = [];
  // the create row is rendered first, so it owns index 0 of the navigable list
  if (freeTextCreateOption.value) out.push(freeTextCreateOption.value);
  const expanded = expandedNodes.value;
  const walk = (nodes: OptionItem[]) => {
    for (const n of nodes) {
      out.push(n);
      const isExp = n.isExpanded === true || expanded.includes(resolveValue(n));
      if (isExp && n.children && n.children.length > 0) {
        walk(n.children);
      }
    }
  };
  walk(displayTree.value);
  return out;
});

// tree-node highlight only — the create row is not a node and tracks its
// highlight through isCreateRowHighlighted instead.
const highlightedNodeId = computed(() => {
  const n = flatVisible.value[highlightedIndex.value];
  if (!n || isFreeTextCreateOption(n)) return null;
  return resolveValue(n);
});

// Find a node anywhere in the tree by value.
const findNodeByValue = (nodes: OptionItem[], value: any): OptionItem | null => {
  for (const n of nodes) {
    if (resolveValue(n) === value) return n;
    if (n.children && n.children.length > 0) {
      const found = findNodeByValue(n.children, value);
      if (found) return found;
    }
  }
  return null;
};

// Find the chain of ancestor values leading to a node (excluding the node
// itself). Used for auto-expanding the path to the initially-selected item.
const findAncestorValues = (nodes: OptionItem[], targetValue: any, chain: any[] = []): any[] | null => {
  for (const n of nodes) {
    if (resolveValue(n) === targetValue) return chain;
    if (n.children && n.children.length > 0) {
      const result = findAncestorValues(n.children, targetValue, [...chain, resolveValue(n)]);
      if (result !== null) return result;
    }
  }
  return null;
};

const expandAncestorsOf = (value: any) => {
  const ancestors = findAncestorValues(props.items, value);
  if (!ancestors || ancestors.length === 0) return;
  const set = new Set<string | number>([...expandedNodes.value, ...ancestors]);
  expandedNodes.value = Array.from(set);
};

// Free-text match indicator for the avatar shown in the collapsed input.
const freeTextMatchedItem = computed(() => {
  if (!props.freeTextEntry || !freeTextValue.value) return null;
  return findNodeByValueOrLabel(props.items, freeTextValue.value);
});

function findNodeByValueOrLabel(nodes: OptionItem[], label: string): OptionItem | null {
  for (const n of nodes) {
    if (resolveLabel(n).toLowerCase() === label.toLowerCase()) return n;
    if (n.children && n.children.length > 0) {
      const found = findNodeByValueOrLabel(n.children, label);
      if (found) return found;
    }
  }
  return null;
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (props.freeTextEntry) {
      if (typeof newVal === 'string') freeTextValue.value = newVal;
      else if (newVal && typeof newVal === 'object') freeTextValue.value = resolveLabel(newVal as OptionItem);
      else freeTextValue.value = '';
    }
  },
  { immediate: true }
);

const selectedOption = computed<OptionItem | null>(() => {
  if (props.freeTextEntry) return null;
  if (props.modelValue === null || props.modelValue === undefined) return null;
  if (typeof props.modelValue === 'string') {
    return findNodeByValue(props.items, props.modelValue) || null;
  }
  if (!props.reduce) return props.modelValue as OptionItem;
  const target = props.modelValue;
  const walk = (nodes: OptionItem[]): OptionItem | null => {
    for (const n of nodes) {
      if (props.reduce!(n) === target) return n;
      if (n.children && n.children.length > 0) {
        const f = walk(n.children);
        if (f) return f;
      }
    }
    return null;
  };
  return walk(props.items);
});

const hasSelection = computed(() => !!selectedOption.value);

const handleFreeTextInput = () => {
  emit('update:modelValue', freeTextValue.value);
  emitSearch(freeTextValue.value);
  if (!isOpen.value && freeTextValue.value) openDropdown();
};

const handleFreeTextEnter = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < flatVisible.value.length) {
    selectItem(flatVisible.value[highlightedIndex.value]);
  } else {
    closeDropdown();
  }
};

watch(searchQuery, (newQuery) => {
  if (!props.freeTextEntry) {
    emitSearch(newQuery);
    highlightedIndex.value = -1;
  }
});

const openDropdown = async () => {
  if (isOpen.value) return;
  openDropdownManager();
  highlightedIndex.value = -1;

  // Auto-expand the path to the currently-selected node so the user sees
  // where they are in the hierarchy when re-opening the dropdown.
  if (selectedOption.value) {
    expandAncestorsOf(resolveValue(selectedOption.value));
  }

  emitSearch(searchValue.value);
  emit('open');
  await nextTick();
  inputRef.value?.focus();
};

const closeDropdown = () => {
  if (!isOpen.value) return;
  closeDropdownManager();
  if (!props.freeTextEntry) searchQuery.value = '';
  highlightedIndex.value = -1;
  scrollBreadcrumb.value = '';
  emit('close');
};

const handleTriggerClick = () => {
  if (props.disabled) return;
  if (props.freeTextEntry) {
    inputRef.value?.focus();
    if (!isOpen.value) openDropdown();
  } else {
    if (isOpen.value) inputRef.value?.focus();
    else openDropdown();
  }
};

const selectItem = (item: OptionItem) => {
  // choosing the synthetic row means "create what I typed", not "select an
  // existing option" — consumers distinguish the two through these events.
  if (isFreeTextCreateOption(item)) {
    emit('option:created', resolveLabel(item));
  } else {
    emit('option:selected', item);
  }

  if (props.freeTextEntry) {
    const label = resolveLabel(item);
    freeTextValue.value = label;
    emit('update:modelValue', label);
  } else {
    const valueToEmit = props.reduce ? props.reduce(item) : item;
    emit('update:modelValue', valueToEmit);
  }
  closeDropdown();
};

const navigate = (direction: 'up' | 'down') => {
  if (!isOpen.value) {
    openDropdown();
    return;
  }
  const total = flatVisible.value.length;
  if (total === 0) return;
  if (direction === 'down') highlightedIndex.value = (highlightedIndex.value + 1) % total;
  else highlightedIndex.value = (highlightedIndex.value - 1 + total) % total;
  scrollHighlightedIntoView();
};

const selectHighlighted = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < flatVisible.value.length) {
    selectItem(flatVisible.value[highlightedIndex.value]);
  }
};

const handleHighlightNode = (node: OptionItem) => {
  const idx = flatVisible.value.findIndex((n) => !isFreeTextCreateOption(n) && resolveValue(n) === resolveValue(node));
  if (idx !== -1) highlightedIndex.value = idx;
};

const toggleExpand = (nodeId: string | number) => {
  if (expandedNodes.value.includes(nodeId)) {
    expandedNodes.value = expandedNodes.value.filter((id) => id !== nodeId);
  } else {
    expandedNodes.value = [...expandedNodes.value, nodeId];
  }
};

const scrollHighlightedIntoView = () => {
  nextTick(() => {
    const scrollArea = scrollAreaRef.value;
    if (!scrollArea) return;
    // the create row sits above the tree, so revealing it means scrolling to top
    if (isCreateRowHighlighted.value) {
      scrollArea.scrollTop = 0;
      return;
    }
    if (highlightedNodeId.value === null || highlightedNodeId.value === undefined) return;
    const el = scrollArea.querySelector<HTMLElement>(`[data-node-id="${CSS.escape(String(highlightedNodeId.value))}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  });
};

// Scroll breadcrumb — ancestors of the node currently at the top of the
// scroll window. Purely informational, mirrors the TreeSelect.vue behavior.
const ancestorMap = computed<Map<any, string[]>>(() => {
  const map = new Map<any, string[]>();
  const walk = (nodes: OptionItem[], chain: string[]) => {
    for (const n of nodes) {
      map.set(resolveValue(n), chain);
      if (n.children && n.children.length > 0) walk(n.children, [...chain, resolveLabel(n)]);
    }
  };
  walk(displayTree.value, []);
  return map;
});

let scrollRafId: number | null = null;
const onDropdownScroll = () => {
  if (scrollRafId !== null) return;
  scrollRafId = requestAnimationFrame(() => {
    scrollRafId = null;
    updateScrollBreadcrumb();
  });
};

const updateScrollBreadcrumb = () => {
  const scrollArea = scrollAreaRef.value;
  if (!scrollArea || scrollArea.scrollTop < 5) {
    scrollBreadcrumb.value = '';
    return;
  }
  const nodeEls = scrollArea.querySelectorAll<HTMLElement>('[data-node-id]');
  const areaTop = scrollArea.getBoundingClientRect().top;
  for (const el of nodeEls) {
    if (el.getBoundingClientRect().top >= areaTop) {
      const raw = el.dataset.nodeId;
      if (raw === undefined) continue;
      const id: any = !isNaN(Number(raw)) ? Number(raw) : raw;
      const ancestors = ancestorMap.value.get(id);
      scrollBreadcrumb.value = ancestors && ancestors.length > 0 ? ancestors.join(' > ') : '';
      return;
    }
  }
  scrollBreadcrumb.value = '';
};

const handleClickOutside = (event: MouseEvent) => {
  if (comboboxRef.value && !comboboxRef.value.contains(event.target as Node)) closeDropdown();
};

const handleInputBlur = (event: FocusEvent) => {
  if (comboboxRef.value && !comboboxRef.value.contains(event.relatedTarget as Node)) closeDropdown();
};

const showClear = computed(() => {
  if (!props.clearable || props.disabled) return false;
  if (props.freeTextEntry) return !!freeTextValue.value;
  return hasSelection.value;
});

const handleClear = (event: MouseEvent) => {
  event.stopPropagation();
  if (props.freeTextEntry) freeTextValue.value = '';
  emit('update:modelValue', null);
  emitSearch(searchValue.value);
  openDropdown();
};

const handleChevronToggle = () => {
  if (props.disabled) return;
  if (isOpen.value) {
    closeDropdown();
    inputRef.value?.blur();
  } else openDropdown();
};

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));

const getInitialsForItem = (item: OptionItem): string =>
  getInitials(item, (i) => resolveLabel(i), props.displayInitials ?? false);

const shouldShowAvatar = (item: OptionItem | null): boolean => {
  if (!item) return false;
  return !!(item.imageUrl || getInitialsForItem(item));
};

const getAvatarBgClass = (item: OptionItem) => {
  if (!item) return 'bg-variant-gray';
  const variant = item.colorVariant || 'gray';
  return `bg-variant-${variant}`;
};
</script>

<style scoped>
.hint-text {
  margin-top: 6px !important;
  font-size: 12px;
  font-weight: 400;
  color: #475467;
}

.combobox-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
  user-select: none;
}

.entity-combobox {
  position: relative;
  width: 100%;
}

.combobox-input-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  padding: 4px 12px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
  box-shadow: 0px 1px 2px 0px #1018280d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.combobox-input-wrapper.has-avatar {
  padding-left: 4px;
}

.combobox-input-wrapper:hover {
  border-color: var(--tblr-primary);
}

.combobox-input-wrapper.is-disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  opacity: 0.7;
}

.combobox-input-wrapper.is-disabled:hover {
  border-color: #d0d5dd;
}

.combobox-input-wrapper.is-open {
  border-color: var(--tblr-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.combobox-input-wrapper.has-error {
  border-color: #ef4444;

  &:hover,
  &.is-open {
    border-color: #ef4444;
  }

  &.is-open {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
  }
}

.combobox-input {
  flex-grow: 1;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  font-size: 16px;
  background: transparent;
  color: #111827;
  height: 100%;
  line-height: 20px;
}

.selection-display {
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;
}

.placeholder-text {
  color: #9ca3af;
  font-size: 14px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-name {
  font-size: 16px;
  font-weight: 400;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chevron-icon {
  width: 20px;
  height: 20px;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.chevron-icon.rotate-180 {
  transform: rotate(180deg);
}

.combobox-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: color 0.2s ease;
}

.combobox-clear:hover {
  color: #111827;
}

.combobox-inline-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.inline-spinner-circle {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--tblr-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.combobox-chevron {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.combobox-chevron:disabled {
  cursor: not-allowed;
}

.combobox-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 250px;
  padding: 0 0 7px 0;
  background-color: #ffffff;
  border: 1px solid var(--tblr-primary);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: 0px 8px 8px 0px #00000026;
  z-index: 50;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tree-scroll-breadcrumb {
  padding: 4px 12px;
  font-size: 12px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  background-color: #fafafa;
}

.dropdown-scroll-area {
  overflow-y: auto;
  flex: 1 1 auto;
  max-height: 243px;
}

.dropdown-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.bg-variant-gray {
  background-color: #e5e7eb;
}

.entity-avatar {
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  overflow: hidden;
  font-weight: 400;
  font-size: 13px;
  color: #000;
}

.entity-avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.entity-avatar .initials {
  color: #000;
  font-weight: 400;
  font-size: 13px;
}

.loading-state {
  padding: 24px 12px;
  text-align: center;
  color: #9ca3af;
}

.spinner {
  margin: 40px auto 0;
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--tblr-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin-top: 8px;
  margin-bottom: 40px;
  font-size: 11px;
  line-height: 24px;
  font-weight: 400;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.dropdown-scroll-area::-webkit-scrollbar {
  width: 6px;
}

.dropdown-scroll-area::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.dropdown-scroll-area::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}
</style>
